import type { Asset, Campaign, CreateCampaignPayload } from '@/services/campaigns/types'
import { sanitizeFile } from './util'
import type { CampaignFormData } from './validations'
import { validateStep } from './validations'

type HandleNextStepArgs = {
  currentStep: number
  formData: Partial<CampaignFormData>
  updateErrors: (errors: Record<string, string>) => void
  updateData: (data: Record<string, unknown>) => void
  nextStep: () => void
  createCampaign: (payload: CreateCampaignPayload) => Promise<Campaign>
  uploadCoverImage: (params: { campaignId: string; imageFile: File }) => Promise<void>
  uploadAssets: (params: { campaignId: string; files: File[] }) => Promise<Array<{ file: File; asset: Asset }>>
}

export const handleNextStep = async ({
  currentStep,
  formData,
  updateErrors,
  updateData,
  nextStep,
  createCampaign,
  uploadCoverImage,
  uploadAssets,
}: HandleNextStepArgs) => {
  const validationResult = validateStep(currentStep, formData)
  if (!validationResult.isValid) {
    updateErrors(validationResult.errors)
    return
  }
  updateErrors({})

  try {
    // Step 3 → Create campaign
    if (currentStep === 3) {
      const createdCampaign = await createCampaign(formData as CreateCampaignPayload)
      updateData({ id: createdCampaign.id })
    }

    // Step 4 → Upload cover image
    if (currentStep === 4) {
      const coverImageFile = formData.coverImageFile as File | undefined
      const campaignId = String(formData.id)
      if (coverImageFile && campaignId) {
        const sanitizedFile = sanitizeFile(coverImageFile)
        await uploadCoverImage({ campaignId, imageFile: sanitizedFile })
      }
    }

    // Step 5 → Upload assets
    if (currentStep === 5) {
      const assetFiles = formData.assetFiles as File[] | undefined
      const assetFilesWithIds = formData.assetFilesWithIds as Array<{ file: File; id?: number }> | undefined
      const campaignId = String(formData.id)
      const uploadedSignatures = formData.uploadedAssetsSignature
        ? new Set(formData.uploadedAssetsSignature.split('||'))
        : new Set<string>()

      if (assetFiles && assetFiles.length > 0 && campaignId) {
        const newFiles = assetFiles.filter((file) => {
          const fileSignature = `${file.name}|${file.size}|${file.type}`
          return !uploadedSignatures.has(fileSignature)
        })

        // Upload only new files if any exist
        if (newFiles.length > 0) {
          const sanitizedFiles = newFiles.map(sanitizeFile)
          const uploadResults = await uploadAssets({ campaignId, files: sanitizedFiles })
          const updatedAssetFilesWithIds = assetFilesWithIds?.map((fileWithId) => {
            const uploadedResult = uploadResults.find(
              (result) => result.file.name === fileWithId.file.name && result.file.size === fileWithId.file.size,
            )
            if (uploadedResult?.asset?.id) return { ...fileWithId, id: uploadedResult.asset.id }
            return fileWithId
          })

          for (const file of newFiles) {
            const fileSignature = `${file.name}|${file.size}|${file.type}`
            uploadedSignatures.add(fileSignature)
          }

          const updatedSignature = Array.from(uploadedSignatures).sort().join('||')
          updateData({
            uploadedAssetsSignature: updatedSignature,
            assetFilesWithIds: updatedAssetFilesWithIds,
          })
        }
      }
    }

    nextStep()
  } catch (error) {
    console.error('Error in step handling:', error)
  }
}
